import { useRef, useState } from 'react';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['.pdf', '.docx', '.txt'];

type DropzoneProps = {
  selectedFile: File | null;
  validationError: string | null;
  onFileSelected: (file: File | null) => void;
  disabled?: boolean;
};

export function validateFile(file: File): string | null {
  const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  if (!ACCEPTED_TYPES.includes(extension)) {
    return 'Unsupported file type. Upload a PDF, DOCX, or TXT document.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'File exceeds the 10 MB upload limit.';
  }
  return null;
}

export function Dropzone({ selectedFile, validationError, onFileSelected, disabled = false }: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleFile(file: File | null) {
    onFileSelected(file);
  }

  return (
    <div className="space-y-4">
      <div
        data-testid="dropzone"
        className={[
          'rounded-[2rem] border-2 border-dashed p-8 transition',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          isDragging ? 'border-teal-700 bg-teal-50' : 'border-slate-300 bg-white/85',
        ].join(' ')}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) {
            setIsDragging(true);
          }
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          if (disabled) {
            return;
          }
          handleFile(event.dataTransfer.files?.[0] ?? null);
        }}
      >
        <div className="space-y-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-700">Academic Integrity Workflow</p>
          <h1 className="text-4xl font-semibold text-slate-950">Upload a document for AI analysis</h1>
          <p className="mx-auto max-w-2xl text-base leading-7 text-slate-600">
            Drag a PDF, DOCX, or TXT file here, or choose one from disk. AI Detect extracts the text,
            analyzes it asynchronously, and returns sentence-level evidence plus a report-ready summary.
          </p>
          <button
            type="button"
            disabled={disabled}
            className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            Choose file
          </button>
          <p className="text-sm text-slate-500">Accepted formats: PDF, DOCX, TXT · Max 10 MB</p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ACCEPTED_TYPES.join(',')}
        disabled={disabled}
        onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
      />

      {selectedFile ? (
        <div className="rounded-[1.5rem] border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">{selectedFile.name}</p>
          <p className="text-sm text-slate-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
        </div>
      ) : null}

      {validationError ? (
        <div className="rounded-[1.25rem] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {validationError}
        </div>
      ) : null}
    </div>
  );
}
